import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Btn, Bx, Typo } from '@curry-group/mui-curcuma';
import { CircularProgress } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faTrashAlt } from '@fortawesome/pro-light-svg-icons';
import { AppFrameContent, AppFrameHeader } from '../../components/frame';
import { AppFrameFooter } from '../../components/frame/footer';
import { Header, ICommunicationAction } from '../../components/communication/header';
import { Messages } from '../../components/communication/messages';
import { CommunicationInput } from '../../components/communication/input';
import { FramedContent } from '../../components/framed-content';
import { GeneralModal } from '../../components/modals/GeneralModal';
import {
  deleteBotChatRequest,
  hydrateBotChatFromSaved,
  sendBotChatMessage,
} from '../../data/actions/botChat';
import { buildBotMessage } from '../../data/reducer/botChat';
import { fetchMessagesRequest } from '../../data/reducer/communication';
import { IMessageModel } from '../../model/communication/Message';
import { containerBasePath } from '../../helper';
import { stripBotTitleSuffix } from '../../model/ryve/Thing';
import { useIsMobile } from '../../helper/hooks/isMobile';
import { useBotChatInfoButton } from './infoButton';
import { BotChatDisclaimer } from './complaint';

const BOT_CHAT_SUBTITLE = 'MTO Medtec Online GmbH';
const MISSING_SESSION_MSG = 'Diese gespeicherte Bot-Session kann nicht fortgesetzt werden, weil keine Session-ID hinterlegt ist. Sie können den bisherigen Verlauf weiter einsehen, neue Nachrichten sind aber nicht möglich.';

/**
 * Defensive reader for `detailItem.content.botSessionId`. We do this in a
 * dedicated function (rather than inline `(x as any)?.y` inside a selector)
 * because the CRA babel transform mishandles optional-chain access through a
 * type assertion mid-expression and crashes with
 * "Cannot read properties of undefined (reading 'botSessionId')".
 */
export function readBotSessionId(content: unknown): string | undefined {
  if (!content || typeof content !== 'object') return undefined;
  const value = (content as Record<string, unknown>).botSessionId;
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

interface IUseDeleteBotChat {
  /** Async-pending state — disable controls. */
  deleting: boolean;
  /** Open the confirm modal. */
  openConfirm: () => void;
  /** Modal element to render. Always include this somewhere in the tree. */
  modal: React.ReactNode;
  /** True when there's a saved chat id we can delete. */
  canDelete: boolean;
}

/**
 * Hook for "delete this saved bot chat". Wires the confirm modal, dispatches
 * deleteBotChatRequest, watches for completion and navigates back to the chat
 * dashboard. Used in both the live detail view and the error-boundary fallback
 * so a broken saved chat is *always* removable.
 */
function useDeleteBotChat(communicationId: string | undefined): IUseDeleteBotChat {
  const dispatch = useDispatch();
  const history = useHistory();
  const deleting = useSelector(state => state.botChat?.deleting) || false;
  const savedCommunicationIdInSlice = useSelector(state => state.botChat?.savedCommunicationId);
  const botBasePath = useSelector(state => containerBasePath(state.foundation?.activeConfig));

  const [confirmOpen, setConfirmOpen] = useState(false);
  const requestedRef = useRef<string | null>(null);

  // When delete success clears the slice, navigate back. We track it via
  // requestedRef so we only navigate for a delete this component initiated.
  useEffect(() => {
    if (
      requestedRef.current
      && !deleting
      && savedCommunicationIdInSlice === undefined
    ) {
      const id = requestedRef.current;
      requestedRef.current = null;
      // Whether the slice cleared via success or via the user discarding, we
      // land back on a fresh bot chat, which also refreshes the sidebar.
      history.push((botBasePath || '') + '/botchat');
      // Bookkeeping: avoid re-firing if the same component re-renders.
      // eslint-disable-next-line no-console
      console.log('Bot chat deletion completed for', id);
    }
  }, [deleting, savedCommunicationIdInSlice, history, botBasePath]);

  const openConfirm = useCallback(() => {
    if (!communicationId) return;
    setConfirmOpen(true);
  }, [communicationId]);

  const confirmDelete = useCallback(() => {
    if (!communicationId) return;
    requestedRef.current = communicationId;
    dispatch(deleteBotChatRequest({ communicationId }));
    setConfirmOpen(false);
  }, [communicationId, dispatch]);

  const cancelDelete = useCallback(() => setConfirmOpen(false), []);

  const modal = (
    <GeneralModal
      open={confirmOpen}
      headline="Bot-Session löschen"
      content="Wollen Sie diese gespeicherte Bot-Session wirklich endgültig löschen? Alle Nachrichten dieser Session werden entfernt und können nicht wiederhergestellt werden."
      commitText="Löschen"
      cancelText="Abbrechen"
      close={cancelDelete}
      cancel={cancelDelete}
      commit={confirmDelete}
    />
  );

  return {
    deleting,
    openConfirm,
    modal,
    canDelete: !!communicationId,
  };
}

interface ISavedBotChatErrorBoundaryProps {
  children?: React.ReactNode;
}
interface ISavedBotChatErrorBoundaryState {
  error?: Error;
}

/**
 * Local error boundary so a faulty saved-chat document never blanks out the
 * entire shell. The fallback always renders a delete button so even broken
 * saves can be cleaned up.
 */
class SavedBotChatErrorBoundary extends React.Component<ISavedBotChatErrorBoundaryProps, ISavedBotChatErrorBoundaryState> {
  state: ISavedBotChatErrorBoundaryState = {};

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('SavedBotChatDetail crashed:', error, info);
  }

  render() {
    if (this.state.error) {
      return <SavedBotChatErrorFallback error={this.state.error} />;
    }
    return this.props.children as any;
  }
}

const SavedBotChatErrorFallback: React.FC<{ error: Error }> = ({ error }) => {
  const isMobile = useIsMobile();
  const detailId = useSelector(state => state.detail?.item?._id);
  const detailTitle = useSelector(state => stripBotTitleSuffix(state.detail?.item?.content?.title));
  const { deleting, openConfirm, modal, canDelete } = useDeleteBotChat(detailId);
  const infoButton = useBotChatInfoButton();

  const metaActions: ICommunicationAction[] = canDelete ? [
    {
      specificType: 'Btn',
      onClick: openConfirm,
      icon: <FontAwesomeIcon icon={faTrashAlt} />,
      title: 'Löschen',
      variant: 'contained',
      color: 'error',
      disabled: deleting,
    },
  ] : [];

  return (
    <>
      <AppFrameHeader>
        <Header
          working={false}
          title={detailTitle || 'Gespeicherter Bot-Chat'}
          subtitle={BOT_CHAT_SUBTITLE}
          // Mobile only: on desktop the session list is always on screen.
          hideBackButton={!isMobile}
          tabs={[]}
          selectedTabValue="main"
          actions={[infoButton.action]}
          metaActions={metaActions}
          metaInfo={<BotChatDisclaimer messages={[]} />}
        />
      </AppFrameHeader>
      <AppFrameContent>
        <FramedContent>
          <Bx display="flex" alignItems="center" justifyContent="center" textAlign="center" p={4}>
            <Bx>
              <Bx mb={2} color="warning.main">
                <FontAwesomeIcon icon={faExclamationTriangle} size="3x" />
              </Bx>
              <Typo variant="h3" gutterBottom>Diese Bot-Session kann nicht angezeigt werden.</Typo>
              <Typo variant="body1" color="textSecondary">{error.message}</Typo>
              {canDelete && (
                <Bx mt={3}>
                  <Btn variant="contained" color="error" onClick={openConfirm} disabled={deleting} startIcon={<FontAwesomeIcon icon={faTrashAlt} />}>
                    {deleting ? 'Wird gelöscht …' : 'Diese Session löschen'}
                  </Btn>
                </Bx>
              )}
            </Bx>
          </Bx>
        </FramedContent>
      </AppFrameContent>
      {modal}
      {infoButton.modal}
    </>
  );
};

const SavedBotChatDetailInner: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const isMobile = useIsMobile();
  const userId = useSelector(state => state.foundation?.profile?.userId) || '';
  // Rubric the saved chat is opened in, so "Neue Session starten" stays inside it.
  const botBasePath = useSelector(state => containerBasePath(state.foundation?.activeConfig));

  // Each detail field is read in its own selector (no inline `as any` mixed
  // with optional chaining — that pattern is mis-transpiled by CRA babel).
  const detailItem = useSelector(state => state.detail?.item);
  const detailId = detailItem?._id;
  const detailTitle = stripBotTitleSuffix(detailItem?.content?.title);
  const botSessionId = readBotSessionId(detailItem?.content);

  // The communication slice is populated by detailSuccessHandler (it dispatches
  // setCommunication) but message loading normally happens inside <ConnectedMessages>
  // mounted by ChatTab. We bypass ChatTab entirely, so we have to dispatch
  // fetchMessagesRequest ourselves once state.communication.id matches our chat.
  const communicationId = useSelector(state => state.communication?.id);
  const savedMessages = useSelector(
    state => state.communication?.messages?.['main']?.messages
  ) as IMessageModel[] | undefined;
  const messagesState = useSelector(state => state.communication?.messages?.['main']);

  const messages = useSelector(state => state.botChat?.messages) || [];
  const currentBotResponse = useSelector(state => state.botChat?.currentBotResponse) || '';
  const botWorking = useSelector(state => state.botChat?.botWorking) || false;
  const savedCommunicationId = useSelector(state => state.botChat?.savedCommunicationId);

  const [inputValue, setInputValue] = useState('');
  const fetchedForRef = React.useRef<string | null>(null);

  const { deleting, openConfirm, modal, canDelete } = useDeleteBotChat(detailId);

  // NOTE: auto-heal on mount has been deactivated by request. The backend
  // endpoint at POST /v2/communication/botchat/{id}/heal still exists and can
  // be invoked manually if a legacy saved bot chat needs its PermissionBag
  // backfilled or its messages re-indexed; opening the chat detail no longer
  // triggers it automatically.

  // Trigger the initial main-channel message fetch once the communication slice
  // has been bound to this chat. Only fires once per detailId so we don't loop.
  useEffect(() => {
    if (!detailId) return;
    if (communicationId !== detailId) return;
    if (fetchedForRef.current === detailId) return;
    if (messagesState && messagesState.messages !== undefined && !messagesState.working) {
      fetchedForRef.current = detailId;
      return;
    }
    if (messagesState?.working) return;
    fetchedForRef.current = detailId;
    dispatch(fetchMessagesRequest({
      reset: true,
      alias: 'main',
      communicationId: detailId,
    }));
  }, [dispatch, detailId, communicationId, messagesState]);

  // Hydrate botChat slice once the messages have actually been loaded.
  useEffect(() => {
    if (!detailId) return;
    if (savedCommunicationId === detailId) return;
    if (!messagesState) return;
    if (messagesState.working) return;
    if (messagesState.messages === undefined) return;
    dispatch(hydrateBotChatFromSaved({
      communicationId: detailId,
      sessionId: botSessionId,
      messages: Array.isArray(savedMessages) ? savedMessages : [],
    }));
  }, [dispatch, detailId, botSessionId, savedMessages, messagesState, savedCommunicationId]);

  const handleSend = useCallback(() => {
    const trimmed = inputValue.trim();
    if (!trimmed || botWorking || !botSessionId || deleting) return;
    dispatch(sendBotChatMessage({ content: trimmed }));
    setInputValue('');
  }, [dispatch, inputValue, botWorking, botSessionId, deleting]);

  const messagesWithStreaming = useMemo<IMessageModel[]>(() => {
    if (botWorking && currentBotResponse) {
      return [...messages, buildBotMessage(currentBotResponse, 'bot-streaming')];
    }
    return messages;
  }, [messages, botWorking, currentBotResponse]);

  const canContinue = !!botSessionId && !deleting;

  const metaActions: ICommunicationAction[] = canDelete ? [
    {
      specificType: 'Btn',
      onClick: openConfirm,
      icon: <FontAwesomeIcon icon={faTrashAlt} />,
      title: deleting ? 'Wird gelöscht …' : 'Löschen',
      variant: 'contained',
      color: 'error',
      disabled: deleting,
    },
  ] : [];

  const infoButton = useBotChatInfoButton();

  return (
    <>
      <AppFrameHeader>
        <Header
          working={!detailId}
          title={detailTitle || 'Medtec Online-Bot'}
          subtitle={BOT_CHAT_SUBTITLE}
          // Mobile only: on desktop the session list is always on screen.
          hideBackButton={!isMobile}
          tabs={[]}
          selectedTabValue="main"
          actions={[infoButton.action]}
          metaActions={metaActions}
          metaInfo={<BotChatDisclaimer messages={messagesWithStreaming} sessionId={botSessionId} />}
        />
      </AppFrameHeader>

      <AppFrameContent>
        <FramedContent
          height="100%"
          minHeight="100%"
          innerBoxProps={{ minHeight: '100%', height: '100%' }}
        >
          <Messages
            currentUserId={userId}
            messages={messagesWithStreaming}
            allowThreading={false}
            allowQuotes={false}
            allowVoting={false}
            inlineThreading={false}
            manageMessages={false}
            isBotChat
            dateDivider
            atStart
            atEnd
            isPublic={false}
            isListed={false}
            onScrollBottomChange={() => {}}
          />
          {botWorking && !currentBotResponse && (
            <Bx display="flex" alignItems="center" mt={1} pl={2}>
              <CircularProgress size={16} color="primary" />
              <Bx ml={1.5}>
                <Typo variant="body2" color="textSecondary">
                  Medtec Online-Bot schreibt ...
                </Typo>
              </Bx>
            </Bx>
          )}
        </FramedContent>
      </AppFrameContent>

      <AppFrameFooter>
        {canContinue ? (
          <CommunicationInput
            placeholder="Nachricht an den Bot verfassen"
            canWriteMessage
            allowAttachments={false}
            disabled={botWorking || deleting}
            value={inputValue}
            onChange={setInputValue}
            onSubmit={handleSend}
          />
        ) : (
          <Bx p={2} bgcolor="background.paper" borderTop="1px solid #DDDFE0">
            <Bx display="flex" alignItems="center">
              <Bx color="warning.main" mr={1.5}>
                <FontAwesomeIcon icon={faExclamationTriangle} />
              </Bx>
              <Bx flexGrow={1}>
                <Typo variant="body2" color="textSecondary">
                  {deleting ? 'Diese Session wird gelöscht …' : MISSING_SESSION_MSG}
                </Typo>
              </Bx>
              {!deleting && (
                <Bx ml={2}>
                  <Btn variant="outlined" color="primary" onClick={() => history.push(botBasePath + '/botchat')}>
                    Neue Session starten
                  </Btn>
                </Bx>
              )}
            </Bx>
          </Bx>
        )}
      </AppFrameFooter>

      {modal}
      {infoButton.modal}
    </>
  );
};

/**
 * Detail view for a *saved* bot chat. Hydrates the botChat slice from the
 * already-loaded saved messages, then renders the same Header + Messages +
 * CommunicationInput triplet as the fresh bot chat. New turns are sent via
 * `sendBotChatMessage`; the botChat saga detects `savedCommunicationId` in
 * state and persists the user+bot pair via `botchat/append` so the chat is
 * fully resumable on reload.
 *
 * Wrapped in a local error boundary so a malformed saved chat never crashes
 * the whole app — the user can navigate away, delete the broken session, etc.
 */
export const SavedBotChatDetail: React.FC = () => (
  <SavedBotChatErrorBoundary>
    <SavedBotChatDetailInner />
  </SavedBotChatErrorBoundary>
);
